import * as React from 'react'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { format, isSameDay } from 'date-fns'
import type { CalendarEvent } from '../../typings'
import { compareEventsByStart } from '../../eventOrder'
import { isMultiDayBar, layoutBarRow } from '../../allDayBars'
import { daysCovered } from '../../eventSpan'
import { useToday } from '../../hooks/useToday'
import { MonthCell, MONTH_ROW_PX } from './MonthCell'
import { PositionedBar } from './PositionedBar'
import { isSameMonth, monthMatrix } from './monthMatrix'

type Props = {
  /** Any day in the month to render. */
  cursor: Date
  events: CalendarEvent[]
  onShowDay?: (day: Date) => void
}

/** Cell padding + date badge + its margin: where the first bar lane starts. */
const BAR_TOP_PX = 4 + 22 + 2

/** Month view — a SUN..SAT header row above a 6×7 grid of day cells. */
export function MonthGrid({ cursor, events, onShowDay }: Props) {
  const theme = useTheme()
  const today = useToday()

  const days = React.useMemo(() => monthMatrix(cursor), [cursor])

  // Bucket each chip event into every day cell it touches (0..41). Sorting first
  // keeps each cell chronological, so the chips shown before "+N more" are the
  // earliest. Multi-day all-day events are drawn as bars instead, one run per week.
  const eventsByDay = React.useMemo(() => {
    const buckets: CalendarEvent[][] = days.map(() => [])
    for (const event of [...events].sort(compareEventsByStart)) {
      if (isMultiDayBar(event)) continue
      for (const i of daysCovered(event, days)) buckets[i].push(event)
    }
    return buckets
  }, [days, events])

  const weekLayouts = React.useMemo(() => {
    const bars = events.filter(isMultiDayBar)
    return Array.from({ length: days.length / 7 }, (_, w) =>
      layoutBarRow(
        bars,
        days.slice(w * 7, w * 7 + 7),
        eventsByDay.slice(w * 7, w * 7 + 7).map((chips) => chips.length),
      ),
    )
  }, [days, events, eventsByDay])

  return (
    <Box
      // `region`, not `grid`: see WeekGrid.
      role="region"
      aria-label="Month view"
      // alignContent 'start' is REQUIRED: without it the grid gets a definite
      // height and every row stretches (LAYOUT-ARCHITECTURE.md §2).
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
        gap: '8px',
        alignContent: 'start',
      }}
    >
      {days.slice(0, 7).map((day) => (
        <Box
          key={`dow-${day.toISOString()}`}
          data-test-id="month-dow"
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            textAlign: 'center',
            pb: '4px',
          }}
        >
          {format(day, 'EEE')}
        </Box>
      ))}

      {days.map((day, i) => {
        const layout = weekLayouts[Math.floor(i / 7)]
        const col = i % 7
        return (
          <MonthCell
            key={day.toISOString()}
            day={day}
            events={eventsByDay[i]}
            inMonth={isSameMonth(day, cursor)}
            isToday={isSameDay(day, today)}
            onShowDay={onShowDay}
            barSlots={layout.slotsByDay[col]}
            hiddenBars={layout.hiddenByDay[col]}
            rowLimit={layout.rowsByDay[col]}
            gridArea={`${Math.floor(i / 7) + 2} / ${col + 1}`}
          />
        )
      })}

      {weekLayouts.flatMap((layout, w) =>
        layout.segments.map((segment) => (
          <PositionedBar
            key={`${segment.event.id}-${w}-${segment.startCol}`}
            segment={segment}
            variant="monthChip"
            firstColumn={1}
            firstRow={w + 2}
            heightPx={MONTH_ROW_PX}
            topOffsetPx={BAR_TOP_PX + segment.lane * (MONTH_ROW_PX + 2)}
          />
        )),
      )}
    </Box>
  )
}
